export type CommandPaletteResolveContext = {
  /** Current location; shared param names (`eventSlug`, `jobPostId`) only fill matching route trees. */
  pathname?: string;
  pgaId?: string;
  sectionId?: string;
  facilityId?: string;
  eventSlug?: string;
  jobPostId?: string;
  universityId?: string;
  activityTypeId?: string;
  activityId?: string;
};

const PARAM_KEYS = [
  "pgaId",
  "sectionId",
  "facilityId",
  "eventSlug",
  "jobPostId",
  "universityId",
  "activityTypeId",
  "activityId"
] as const;

export type CommandPaletteParamKey = (typeof PARAM_KEYS)[number];

const PARAM_KEY_SET = new Set<string>(PARAM_KEYS);

function isCommandPaletteParamKey(key: string): key is CommandPaletteParamKey {
  return PARAM_KEY_SET.has(key);
}

function routeParam(value: string | undefined): string | undefined {
  return value && value.length > 0 ? value : undefined;
}

/**
 * Build resolve context from the current route only. Do not copy section/university
 * ids from profile state, and do not reuse `eventSlug` / `jobPostId` across trees.
 */
export function buildCommandPaletteResolveContext(
  params: Readonly<Record<string, string | undefined>>,
  pathname: string
): CommandPaletteResolveContext {
  const context: CommandPaletteResolveContext = { pathname };
  for (const key of PARAM_KEYS) {
    context[key] = routeParam(params[key]);
  }
  return context;
}

function paramAppliesToDestination(path: string, key: CommandPaletteParamKey, pathname: string | undefined): boolean {
  if (!pathname) return true;

  if (key === "eventSlug") {
    if (path.includes("/activities/events/:eventSlug")) {
      return pathname.startsWith("/activities/events/");
    }
    if (path.includes("/education/universities/") && path.includes("/events/:eventSlug")) {
      return /^\/education\/universities\/[^/]+\/events(?:\/|$)/.test(pathname);
    }
    if (path.startsWith("/events/:eventSlug")) {
      return pathname.startsWith("/events/") && !pathname.startsWith("/events/pat-results");
    }
    return false;
  }

  if (key === "jobPostId") {
    if (path.includes("/sections/:sectionId/jobs/job-post/:jobPostId")) {
      return /^\/sections\/[^/]+\/jobs\/job-post(?:\/|$)/.test(pathname);
    }
    if (path.includes("/facilities/:facilityId/jobs/job-post/:jobPostId")) {
      return /^\/facilities\/[^/]+\/jobs\/job-post(?:\/|$)/.test(pathname);
    }
    if (path.startsWith("/jobs/job-post/:jobPostId")) {
      return pathname.startsWith("/jobs/job-post/");
    }
    return false;
  }

  return true;
}

function fallbackForPath(path: string): string {
  if (path.startsWith("/people/:pgaId")) return "/people";
  if (path.startsWith("/sections/:sectionId")) return "/sections";
  if (path.startsWith("/facilities/:facilityId")) return "/facilities";
  if (path.startsWith("/activities/events/:eventSlug")) return "/activities/events";
  if (path.startsWith("/events/:eventSlug")) return "/events";
  if (path.startsWith("/jobs/job-post/:jobPostId")) return "/jobs";
  if (path.startsWith("/education/universities/:universityId")) return "/education/universities";
  if (path.startsWith("/activities/activity-types/:activityTypeId/activity/:activityId")) {
    return "/activities/activity-types";
  }
  if (path.startsWith("/activities/activity-types/:activityTypeId")) return "/activities/activity-types";
  return path.replace(/\/:[^/]+/g, "");
}

export function resolveCommandPalettePath(
  path: string,
  context: CommandPaletteResolveContext,
  fallbackPath?: string
): string {
  const required = [...path.matchAll(/:([A-Za-z]+)/g)].map((match) => match[1]);
  if (required.length === 0) return path;

  let resolved = path;
  for (const key of required) {
    if (!isCommandPaletteParamKey(key)) {
      return fallbackPath ?? fallbackForPath(path);
    }
    if (!paramAppliesToDestination(path, key, context.pathname)) {
      return fallbackPath ?? fallbackForPath(path);
    }
    const value = context[key];
    if (!value) return fallbackPath ?? fallbackForPath(path);
    resolved = resolved.replace(new RegExp(`:${key}\\b`), encodeURIComponent(value));
  }

  return resolved;
}
